import { SnCExcelSourceField } from "../enums/sncExcel";
import { normalizeSheetRows, updateFieldValue } from "./payloadRows";
import { isSampleTestRow } from "./sampleDataFilter";

/** `"CODE - Name"` → `CODE`; plain values pass through. */
const extractCode = (value: unknown): string => {
  if (value == null) {
    return "";
  }
  return String(updateFieldValue(value) ?? "").trim();
};

const isExportableRow = (
  row: Record<string, unknown>,
  codeField: string,
): boolean =>
  !isSampleTestRow(row) && Boolean(extractCode(row[codeField]));

const excelGroupNumber = (
  row: Record<string, unknown>,
  index: number,
): number => {
  const id = row.id;
  if (typeof id === "number" && Number.isFinite(id)) {
    return id;
  }
  if (id != null && String(id).trim() !== "") {
    const parsed = Number(id);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return index + 1;
};

/**
 * Catalog item main rows (AV / Beverage / Misc / Setup).
 * Sets EXCEL_GROUP_NUMBER so measurement / revenue sheets can link.
 */
export const mapSnCCatalogItemRows =
  (codeField: string) =>
  (sheetData: unknown): Record<string, unknown>[] =>
    normalizeSheetRows(sheetData)
      .filter((row) => isExportableRow(row, codeField))
      .map((row, index) => ({
        ...row,
        EXCEL_GROUP_NUMBER: excelGroupNumber(row, index),
      }));

/** Format selected codes as `[CODE]` or `[CODE1,CODE2]`. */
const formatBracketCodes = (value: unknown): string => {
  const values = Array.isArray(value) ? value : value != null ? [value] : [];
  const codes = values
    .map((entry) => extractCode(entry))
    .filter((code) => Boolean(code));
  return `[${codes.join(",")}]`;
};

const readMultiSelectValues = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value == null || value === "") {
    return [];
  }
  return [value];
};

/** Revenue Type / Item may be `"123 - Description"` — export item number only. */
const extractRetailItemNumber = (value: unknown): number | string | null => {
  const code = extractCode(value);
  if (!code) {
    return null;
  }
  const numeric = Number(code);
  return Number.isNaN(numeric) ? code : numeric;
};

/**
 * One FunctionItemRevenuetype / catalog revenue row per Building + retail item.
 * Columns: EXCEL_GROUP_NUMBER, ITEM_CODE, ITEM_NUMBER, BUILDING_CODE.
 */
export const mapSnCRevenueTypeRows =
  (codeField: string, revenueItemField: string, nameField?: string) =>
  (sheetData: unknown): Record<string, unknown>[] => {
    const rows: Record<string, unknown>[] = [];

    normalizeSheetRows(sheetData).forEach((item, index) => {
      if (!isExportableRow(item, codeField)) {
        return;
      }

      const group = excelGroupNumber(item, index);
      const revenueTypes = Array.isArray(item["Revenue Types"])
        ? item["Revenue Types"]
        : [];

      for (const raw of revenueTypes) {
        if (!raw || typeof raw !== "object") {
          continue;
        }
        const revenue = raw as Record<string, unknown>;
        const building = extractCode(revenue.Building);
        const itemNumber = extractRetailItemNumber(
          revenue[revenueItemField] ??
            revenue[SnCExcelSourceField.RevenueItem] ??
            revenue[SnCExcelSourceField.ItemCode],
        );
        if (!building && itemNumber == null) {
          continue;
        }

        rows.push({
          EXCEL_GROUP_NUMBER: group,
          [codeField]: item[codeField],
          ...(nameField ? { [nameField]: item[nameField] } : {}),
          Building: building || null,
          [revenueItemField]: itemNumber,
        });
      }
    });

    return rows;
  };

/** Menu Item sheet — food type codes in brackets; presentation left empty via column default. */
export const mapSnCMenuItemRows = (
  sheetData: unknown,
): Record<string, unknown>[] =>
  normalizeSheetRows(sheetData)
    .filter((row) => isExportableRow(row, SnCExcelSourceField.MenuItemCode))
    .map((row) => ({
      ...row,
      [SnCExcelSourceField.FoodTypes]: formatBracketCodes(
        row[SnCExcelSourceField.FoodTypes] ??
          row[SnCExcelSourceField.FoodType],
      ),
    }));

/**
 * One Menu_Item_Measurement row per selected unit of measure.
 * COST / PRICE / COUNT_BY_COVERS come from column defaults (0 / 0 / False).
 */
export const mapSnCMenuMeasurementRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  normalizeSheetRows(sheetData).forEach((item) => {
    if (!isExportableRow(item, SnCExcelSourceField.MenuItemCode)) {
      return;
    }

    const units = readMultiSelectValues(
      item[SnCExcelSourceField.UnitsOfMeasure] ??
        item[SnCExcelSourceField.UnitOfMeasure],
    )
      .map((entry) => extractCode(entry))
      .filter((code) => Boolean(code));

    units.forEach((unitCode) => {
      rows.push({
        [SnCExcelSourceField.MenuItemCode]:
          item[SnCExcelSourceField.MenuItemCode],
        [SnCExcelSourceField.MenuItemName]:
          item[SnCExcelSourceField.MenuItemName],
        [SnCExcelSourceField.UnitOfMeasure]: unitCode,
      });
    });
  });

  return rows;
};

/**
 * One Menu_Item_Revenue_Type row per Building + retail item on the menu item.
 * RETAIL_ITEM_NUMBER is the item number only (no description).
 */
export const mapSnCMenuRevenueTypeRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  normalizeSheetRows(sheetData).forEach((item) => {
    if (!isExportableRow(item, SnCExcelSourceField.MenuItemCode)) {
      return;
    }

    const revenueTypes = Array.isArray(item["Revenue Types"])
      ? item["Revenue Types"]
      : [];

    for (const raw of revenueTypes) {
      if (!raw || typeof raw !== "object") {
        continue;
      }
      const revenue = raw as Record<string, unknown>;
      const building = extractCode(revenue.Building);
      const itemNumber = extractRetailItemNumber(
        revenue[SnCExcelSourceField.ItemCode] ??
          revenue[SnCExcelSourceField.RevenueItem],
      );
      if (!building && itemNumber == null) {
        continue;
      }

      rows.push({
        [SnCExcelSourceField.MenuItemCode]:
          item[SnCExcelSourceField.MenuItemCode],
        [SnCExcelSourceField.MenuItemName]:
          item[SnCExcelSourceField.MenuItemName],
        [SnCExcelSourceField.Building]: building || null,
        [SnCExcelSourceField.ItemCode]: itemNumber,
      });
    }
  });

  return rows;
};

const toIsoDate = (value: unknown): string | null => {
  if (value == null || String(value).trim() === "") {
    return null;
  }
  const raw = String(value).trim();
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const mapHolidayLineType = (value: unknown): string | null => {
  const text = extractCode(value);
  if (!text) {
    return null;
  }
  if (text === "Hot Dates" || text === "HotDates") {
    return "HotDates";
  }
  if (text === "Holidays" || text === "Holiday") {
    return "Holidays";
  }
  return text.replace(/\s+/g, "");
};

const mapHolidayDispLocation = (value: unknown): string | null => {
  const text = extractCode(value);
  if (!text) {
    return null;
  }
  if (text.startsWith("Guest")) {
    return "Guest";
  }
  if (text === "Function Book") {
    return "FunctionBook";
  }
  if (text === "All") {
    return "All";
  }
  return text;
};

/** Holiday sheet rows — normalize dates to YYYY-MM-DD and Visual One line/location codes. */
export const mapSnCHolidayRows = (
  sheetData: unknown,
): Record<string, unknown>[] =>
  normalizeSheetRows(sheetData)
    .filter((row) =>
      isExportableRow(row, SnCExcelSourceField.HolidayHotDateCode),
    )
    .map((row) => ({
      ...row,
      "Display Line": mapHolidayLineType(row["Display Line"]),
      "Display Location": mapHolidayDispLocation(row["Display Location"]),
      "Start Date": toIsoDate(row["Start Date"]),
      "End Date": toIsoDate(row["End Date"]),
    }));

/** Function Room sheet — exact Visual One columns / defaults. */
export const mapSnCFunctionRoomRows = (
  sheetData: unknown,
): Record<string, unknown>[] =>
  normalizeSheetRows(sheetData)
    .filter((row) =>
      isExportableRow(row, SnCExcelSourceField.FunctionRoomCode),
    )
    .map((row) => ({
      ...row,
      [SnCExcelSourceField.Building]:
        extractCode(row[SnCExcelSourceField.Building]) || null,
      [SnCExcelSourceField.RevenueType]: extractRetailItemNumber(
        row[SnCExcelSourceField.RevenueType] ??
          row[SnCExcelSourceField.RevenueItem],
      ),
      [SnCExcelSourceField.RoomFeatures]: formatBracketCodes(
        row[SnCExcelSourceField.RoomFeatures],
      ),
    }));

